/**
 * Thành phần giao diện dùng chung của web quầy (ADR-036).
 *
 * Mỗi thứ ở đây tồn tại để thay một control thô của trình duyệt hoặc một kiểu tự chế đã bị
 * `ui-critic` bắt ở vòng 0: ô giờ AM/PM, checkbox 13px, chữ "Đang tải…" trơ trọi, màn trắng.
 */
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react'
import {
  Component,
  type ErrorInfo,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
} from 'react'
import { gioPhut } from './api.ts'
import { LienHeSapaKhoi } from './LienHeSapa.tsx'
import { useBanMoi } from './pwa.ts'
import { T } from './text/vi.ts'

/**
 * Dải báo "Có bản mới của SAPA" + nút Cập nhật (docs/plans/nut-cap-nhat-ban-moi-web-quay.md). Nằm TRONG luồng trang, không
 * che gì, không có nút "Để sau". Chưa có bản mới thì không vẽ gì.
 */
export function DaiBanMoi() {
  const { co, dang, loi, capNhat } = useBanMoi()
  if (!co) return null
  return (
    <div className="dai-bao-han" data-muc="ban-moi" role="status">
      {loi ? (
        <span>{T.banMoiMatMang}</span>
      ) : (
        <>
          <span className="chu-day">{T.banMoiCo}</span>
          {/* Câu ngắn chỉ hiện ở màn Lịch dưới 340px (styles.css); ẩn bằng CSS nên trình đọc màn hình chỉ đọc một câu. */}
          <span className="chu-ngan">{T.banMoiCoNgan}</span>
        </>
      )}
      <button
        type="button"
        className="nut nut-chinh"
        disabled={dang}
        onClick={() => void capNhat()}
      >
        {dang ? T.banMoiDang : T.banMoiNut}
      </button>
    </div>
  )
}

/**
 * Dòng phụ gồm vài cụm ngắn ngăn cách bằng "·" ("0952 931 203 · Tới 27/09", "120.000đ · 45 phút").
 * Mỗi cụm đi liền nhau; dấu "·" nằm ĐẦU cụm sau và bị cắt khi cụm đó xuống đầu dòng — dấu không bao giờ mồ côi ở cuối
 * hay đầu dòng (ui-critic vòng 45 / 47: "Gội đầu dưỡng sinh · / 120.000đ"). Cụm rỗng bị bỏ, kèm cả dấu của nó.
 * Số + danh từ trong một cụm dùng khoảng trắng KHÔNG NGẮT (U+00A0) ở text/*.ts. Dùng `<bdi>` (không phải `<span>`) để
 * luật `.muc .chu span` / `.dong-khach .chu span` của hàng danh sách không áp lên khối này.
 */
export function CacCum({ cac }: { cac: ReadonlyArray<ReactNode> }) {
  const co = cac.filter((x) => x !== null && x !== undefined && x !== false && x !== '')
  if (co.length === 0) return null
  return (
    <bdi className="cac-cum">
      {co.map((c, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: danh sách cụm cố định theo thứ tự, không đổi chỗ.
        <bdi key={i} className="cum">
          {c}
        </bdi>
      ))}
    </bdi>
  )
}

/** Logo SAPA: tờ lịch có dấu tích trong ô bo góc — nhận ra được ở 32px. */
export function Logo({ co = 40 }: { co?: number }) {
  /*
   * id RIÊNG cho mỗi logo: màn đăng nhập có hai logo, logo đầu nằm trong khối bị ẩn trên điện thoại,
   * và `url(#id)` trỏ vào gradient của khối bị ẩn thì không vẽ — logo điện thoại tàng hình (vòng 1).
   * Hình tờ lịch có dấu tích, màu xanh lam của icon (01/10, ADR-082; 02/10 màu chủ đạo web quầy `--sp-primary` cũng lấy từ đây): cùng logo
   * Mini App Center + icon PWA (`public/icon.svg`) — không còn bông sen (biểu tượng spa).
   * CỐ Ý (ADR-115, v0.23.0): đây là logo công cụ SAPA, KHÔNG theo ngành của tiệm — màu ngành chỉ đổi nút / mục đang chọn / liên kết,
   * logo giữ xanh SAPA để người dùng luôn thấy "đang ở SAPA, ở cửa hàng X". Đừng nối nó vào `--sp-primary`.
   */
  const id = useId()
  return (
    <svg width={co} height={co} viewBox="0 0 40 40" role="img" aria-label={T.brand}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#4f80c4" />
          <stop offset="1" stopColor="#234a85" />
        </linearGradient>
      </defs>
      <rect width="40" height="40" rx="12" fill={`url(#${id})`} />
      <g
        transform="translate(6.8 6.6) scale(1.1)"
        fill="none"
        stroke="#fff"
        strokeWidth={1.9}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3.5" y="5" width="17" height="15.5" rx="2.6" fill="#fff" fillOpacity={0.14} />
        <path d="M8 3v4M16 3v4M3.5 10h17" />
        <path d="M8.6 15.1l2.3 2.3 4.6-4.6" strokeWidth={2.2} />
      </g>
    </svg>
  )
}

/** Chữ cái đầu của tên để làm avatar: "Lê Minh Tú" -> "T" (tên gọi của người Việt ở cuối). */
export function chuDau(ten: string): string {
  const tu = ten.trim().split(/\s+/)
  const cuoi = tu[tu.length - 1] ?? ''
  return (cuoi[0] ?? '?').toUpperCase()
}

/** Màu avatar ổn định theo tên: cùng một người luôn cùng một màu. */
export function lopMau(ten: string): string {
  let h = 0
  for (const c of ten) h = (h * 31 + (c.codePointAt(0) ?? 0)) >>> 0
  return `av-${(h % 6) + 1}`
}

export function Avatar({ ten, co = '' }: { ten: string; co?: '' | 'nho' | 'lon' }) {
  return (
    <span className={`avatar ${co} ${lopMau(ten)}`} aria-hidden="true">
      {chuDau(ten)}
    </span>
  )
}

export function ThongBao({
  loai,
  children,
  onDong,
  noi = false,
  lon = false,
  hanhDong,
  khan = false,
  lop = '',
}: {
  loai: 'loi' | 'thanh-cong' | 'canh-bao' | 'thong-tin'
  /** Thêm class riêng cho MỘT thông báo (ví dụ bố cục lưới của báo "Đã chuyển sang …"); không đổi các thông báo khác. */
  lop?: string
  children: ReactNode
  onDong?: () => void
  noi?: boolean
  /** Lỗi cần đọc ngay (người dùng vừa bấm xong): `role="alert"` thay cho `role="status"`. Mặc định không đổi hành vi nơi khác. */
  khan?: boolean
  /** Chữ 16px + nút đóng ≥ 44px — màn mở tiệm / thẻ Chuẩn bị tiệm / Mã QR (CLAUDE.md §7). */
  lon?: boolean
  /** Một nút nhỏ trong thông báo, ví dụ "Hoàn tác". */
  hanhDong?: ReactNode
}) {
  return (
    <div
      className={`thong-bao ${loai} ${noi ? 'noi-thong-bao' : ''} ${lon ? 'thong-bao-lon' : ''}${lop === '' ? '' : ` ${lop}`}`}
      role={khan ? 'alert' : 'status'}
    >
      {loai === 'thanh-cong' ? (
        <CheckCircle2 size={18} />
      ) : loai === 'thong-tin' ? (
        <Info size={18} />
      ) : (
        <AlertCircle size={18} />
      )}
      <div>{children}</div>
      {hanhDong}
      {onDong !== undefined && (
        <button type="button" className="dong" onClick={onDong} aria-label={T.close}>
          <X size={18} />
        </button>
      )}
    </div>
  )
}

/** Màn rỗng: icon + một câu nói điều gì sẽ ở đây + đúng một hành động (THAM-KHAO §3.4). */
export function Rong({
  icon,
  tieuDe,
  moTa,
  hanhDong,
}: {
  icon: ReactNode
  tieuDe: string
  moTa?: string
  hanhDong?: ReactNode
}) {
  return (
    <div className="rong">
      <span className="vong-icon">{icon}</span>
      <h3>{tieuDe}</h3>
      {moTa !== undefined && <p>{moTa}</p>}
      {hanhDong}
    </div>
  )
}

/** Khung xương lúc đang tải — thay cho chữ "Đang tải…" trơ trọi. */
export function Xuong({ cao = 16, rong = '100%' }: { cao?: number; rong?: number | string }) {
  return <div className="xuong" style={{ height: cao, width: rong }} />
}

/**
 * Mở bảng chọn ngày của máy khi bấm vào ô ngày phủ trong suốt lên nút. Chrome trên máy tính / tablet chỉ mở
 * bảng khi bấm đúng biểu tượng lịch nhỏ ở mép phải ô — ô phủ trong suốt thì bấm vào đâu cũng chỉ đặt con trỏ
 * vào ngày / tháng / năm vô hình, nên "chọn ngày không được" (phản ánh 30/09).
 */
export function moBangChonNgay(el: HTMLInputElement): void {
  try {
    el.showPicker()
  } catch {
    // Trình duyệt cũ không có showPicker: để mặc định của máy.
  }
}

export function CongTac({
  bat,
  onDoi,
  nhan,
  tat = false,
}: {
  bat: boolean
  onDoi: (bat: boolean) => void
  nhan: ReactNode
  tat?: boolean
}) {
  return (
    <label className="cong-tac">
      <input
        type="checkbox"
        checked={bat}
        disabled={tat}
        onChange={(e) => onDoi(e.target.checked)}
      />
      <span className="ray" />
      <span>{nhan}</span>
    </label>
  )
}

/**
 * Chọn giờ dạng 24h, bước 15 phút.
 *
 * Không dùng `<input type="time">`: trình duyệt hiện theo ngôn ngữ của máy, và trên máy tiếng Anh
 * là "08:00 AM" — `ui-critic` bắt đúng lỗi đó ở vòng 0. Một danh sách giờ cố định thì ở đâu cũng
 * là "08:00".
 */
const CAC_MOC = Array.from({ length: (24 * 60) / 15 + 1 }, (_, i) => i * 15)

export function ChonGio({
  phut,
  onDoi,
  nhan,
  tat = false,
}: {
  phut: number
  onDoi: (phut: number) => void
  nhan: string
  tat?: boolean
}) {
  return (
    <select
      className="o-chon gon"
      aria-label={nhan}
      value={phut}
      disabled={tat}
      onChange={(e) => onDoi(Number.parseInt(e.target.value, 10))}
    >
      {CAC_MOC.map((m) => (
        <option key={m} value={m}>
          {gioPhut(m)}
        </option>
      ))}
    </select>
  )
}

/** Số lớp (tờ, menu) đang khoá cuộn thân trang; nhả khi về 0 để hai lớp chồng nhau không mở khoá của nhau. */
let soLopKhoaCuon = 0
/** Khoá cuộn thân trang; trả hàm nhả (dùng làm cleanup của `useEffect`). */
export function khoaCuonTrang(): () => void {
  soLopKhoaCuon += 1
  document.documentElement.classList.add('khoa-cuon')
  let daNha = false
  return () => {
    if (daNha) return
    daNha = true
    soLopKhoaCuon = Math.max(0, soLopKhoaCuon - 1)
    if (soLopKhoaCuon === 0) document.documentElement.classList.remove('khoa-cuon')
  }
}

/**
 * Tờ (sheet): bên phải trên tablet, trượt từ dưới lên trên điện thoại. Nền mờ là **một nút Đóng
 * thật** chứ không phải `div` bắt sự kiện — bấm ra ngoài hay nhấn Esc đều đóng.
 */
export function To({
  nhan,
  onDong,
  children,
}: {
  nhan: string
  onDong: () => void
  children: ReactNode
}) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDong()
    }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [onDong])
  // Vòng 49: cuộn trong tờ không được kéo trang phía sau (đóng tờ xong Lịch nằm ở chỗ khác).
  useEffect(() => khoaCuonTrang(), [])
  return (
    <div className="lop-phu">
      <button
        type="button"
        className="nen-dong"
        aria-label={T.close}
        tabIndex={-1}
        onClick={onDong}
      />
      <section className="to" role="dialog" aria-modal="true" aria-label={nhan}>
        {children}
      </section>
    </div>
  )
}

/**
 * Dấu hiệu trên lịch: "Đã đổi giờ" (kèm giờ cũ khi có chỗ) và "Khách đã xác nhận" / "Chưa xác nhận".
 * Trước đây đổi giờ xong trên lịch không còn dấu vết nào — chủ tiệm không biết lịch này đã bị dời (phản ánh 30/09).
 * Đổi giờ xoá xác nhận của giờ cũ nên hai dấu này hay đi cùng nhau: "Đã đổi giờ" + "Chưa xác nhận".
 */
export function DauTrangThaiLich({
  soLanDoi,
  gioCu,
  daXacNhan,
  chuaXacNhan,
  ngan = false,
}: {
  soLanDoi?: number | undefined
  gioCu?: string | null | undefined
  daXacNhan?: boolean | undefined
  chuaXacNhan?: boolean | undefined
  /** Khối lịch nhỏ trên lưới: không kèm giờ cũ. */
  ngan?: boolean
}) {
  const doi = (soLanDoi ?? 0) > 0
  if (!doi && daXacNhan !== true && chuaXacNhan !== true) return null
  return (
    <span className={ngan ? 'dau-lich ngan' : 'dau-lich'}>
      {doi && (
        <span
          className="nhan-doi-gio"
          title={gioCu ? `${T.rescheduledFrom} ${gioCu}` : T.rescheduled}
        >
          {T.rescheduled}
          {!ngan && gioCu ? (
            <>
              {` · ${T.rescheduledFrom} `}
              {/* Giờ + ngày cũ ("15:15 29/10") đi liền một dòng, không tách ở 320 / 360 / 768px (v0.23.2 vòng sửa 1, N3). */}
              <span className="khong-be">{gioCu}</span>
            </>
          ) : (
            ''
          )}
        </span>
      )}
      {daXacNhan === true && <span className="nhan-xac-nhan ok">{T.attendanceConfirmed}</span>}
      {chuaXacNhan === true && (
        <span className="nhan-xac-nhan chua">{T.attendanceUnconfirmed}</span>
      )}
    </span>
  )
}

/**
 * Rào lỗi: một màn vẽ hỏng thì hiện câu tiếng Việt + nút "Tải lại", không để cả web quầy trắng xoá (phản ánh
 * 30/09: bấm thông báo → trắng màn hình, chủ tiệm không biết làm gì tiếp).
 */
export class RaoLoi extends Component<
  { children: ReactNode; khiDong?: () => void; khiVe?: () => void },
  { hong: boolean }
> {
  override state = { hong: false }

  static getDerivedStateFromError(): { hong: boolean } {
    return { hong: true }
  }

  override componentDidCatch(loi: Error, thongTin: ErrorInfo): void {
    // Chỉ để lại dấu ở console trình duyệt cho người hỗ trợ; không gửi đi đâu.
    console.error('Màn hình bị lỗi', loi.message, thongTin.componentStack)
  }

  override render(): ReactNode {
    if (!this.state.hong) return this.props.children
    return (
      <div className="rao-loi" role="alert">
        <p>
          <b>{T.screenBroken}</b>
        </p>
        <p className="chu-phu">{T.screenBrokenHint}</p>
        <button
          type="button"
          className="nut nut-chinh nut-lon"
          onClick={() => window.location.reload()}
        >
          {T.reload}
        </button>
        {this.props.khiVe !== undefined && (
          <button type="button" className="nut nut-vien nut-lon" onClick={this.props.khiVe}>
            {T.backToStart}
          </button>
        )}
        {this.props.khiDong !== undefined && (
          <button type="button" className="nut nut-vien" onClick={this.props.khiDong}>
            {T.close}
          </button>
        )}
        {/* Màn hỏng là lúc người dùng cần biết gọi ai nhất (đợt 3 · 3.2). */}
        <LienHeSapaKhoi kieu="cau" />
      </div>
    )
  }
}

/**
 * Chỉ nhận kết quả của lần tải MỚI NHẤT. Bấm đổi ngày / bộ lọc liên tiếp thì phản hồi của lần cũ có thể về SAU
 * lần mới và đè lên — màn hình ghi ngày này mà số liệu là của ngày khác (phản ánh 30/09: "chọn ngày không được").
 * Dùng: `const conMoi = lanMoi(); const kq = await api…; if (!conMoi()) return; setData(kq)`.
 */
export function useLanTaiMoiNhat(): () => () => boolean {
  const so = useRef(0)
  return useCallback(() => {
    so.current += 1
    const cua = so.current
    return () => cua === so.current
  }, [])
}
